import jsQR from "jsqr";

/// Leitura de código pela câmera.
///
/// Dois motores, nesta ordem:
///
/// 1. `BarcodeDetector`, nativo do navegador. Lê QR **e** códigos de barras
///    lineares — e isso importa: a etiqueta da Shopee (`BR…C`) é linear, não
///    QR. Onde ele existe, é o único caminho que lê os três formatos.
/// 2. `jsQR`, por software. Só decodifica QR, então cobre Mercado Livre e Loja
///    Integrada, mas **não** a Shopee.
///
/// Consequência prática, e é preciso ser honesto sobre ela: em aparelhos sem
/// `BarcodeDetector` (iPhone, Firefox), a etiqueta da Shopee não vai ler pela
/// câmera. O botão de digitar o código continua valendo para esses casos, e é
/// por isso que ele não é um recurso escondido.

type Detected = { value: string };

interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<Array<{ rawValue: string }>>;
}

type BarcodeDetectorCtor = new (options?: { formats?: string[] }) => BarcodeDetectorLike;

const FORMATS = ["qr_code", "code_128", "code_39", "ean_13", "itf"];

export type ScannerEngine = "nativo" | "jsqr";

export function engineAvailable(): ScannerEngine {
  return "BarcodeDetector" in window ? "nativo" : "jsqr";
}

async function makeDetector(): Promise<BarcodeDetectorLike | null> {
  const Ctor = (window as unknown as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;
  if (!Ctor) return null;

  try {
    return new Ctor({ formats: FORMATS });
  } catch {
    // Alguns navegadores anunciam a API mas recusam a lista de formatos.
    // Sem filtro, aceitam tudo que sabem ler.
    try {
      return new Ctor();
    } catch {
      return null;
    }
  }
}

export type StopScanning = () => void;

/// Liga a câmera traseira e chama `onRead` a cada código reconhecido.
///
/// O mesmo código lido em quadros seguidos dispara UMA vez: a câmera devolve
/// 30 quadros por segundo e a etiqueta fica parada na frente dela por vários
/// deles. Sem essa trava, uma leitura viraria dezenas de requisições.
export async function startScanning(
  video: HTMLVideoElement,
  onRead: (code: string) => void,
  onError: (message: string) => void
): Promise<StopScanning> {
  let stopped = false;
  let stream: MediaStream | null = null;
  let frame = 0;

  let lastValue = "";
  let lastAt = 0;
  const REPEAT_BLOCK_MS = 2500;

  // Um código só é aceito depois de decodificar IGUAL em dois quadros
  // seguidos — não no primeiro.
  //
  // Achado num caso real (03/09): cinco leituras erradas em vinte segundos,
  // cada uma um número diferente ("27908229108651", "409029108651",
  // "29108651"…), entre duas leituras corretas de 6 dígitos. Nenhuma batia
  // com a anterior, então a trava de repetição acima — que só bloqueia o
  // MESMO valor de novo — nunca entrava em ação: para o sistema, cada uma era
  // um código novo, e virava um "pedido" fantasma na tela sem nome de
  // cliente, sem avisar duplicata nenhuma porque não havia duplicata — só
  // ruído. É a marca de uma etiqueta com mais de um código de barras: a
  // câmera pega um pedaço de um código maior enquanto o operador reposiciona,
  // e um pedaço de código de barras às vezes ainda fecha checksum sozinho.
  //
  // Exigir a mesma leitura duas vezes seguidas filtra isso: ruído de
  // enquadramento não costuma repetir o mesmo erro duas vezes por acaso, e
  // uma etiqueta parada de verdade decodifica igual quadro após quadro — o
  // atraso é de um trigésimo de segundo, imperceptível.
  let candidata: string | null = null;

  const emit = ({ value }: Detected) => {
    if (candidata !== value) {
      candidata = value;
      return;
    }

    const now = Date.now();
    if (value === lastValue && now - lastAt < REPEAT_BLOCK_MS) return;
    lastValue = value;
    lastAt = now;
    candidata = null;
    onRead(value);
  };

  try {
    stream = await navigator.mediaDevices.getUserMedia({
      // Sem isso, o iPhone abre a câmera na resolução mais alta que tem —
      // e o caminho sem `BarcodeDetector` (jsQR, puro JavaScript) decodifica
      // cada quadro nessa resolução inteira, a cada frame. 1280x720 já é
      // mais que suficiente pra ler um QR a distância de bipagem normal, e
      // corta a quantidade de pixel que o jsQR precisa varrer por quadro —
      // é essa conta, não a câmera em si, que deixava a leitura lenta no
      // iPhone (pedido do Abner em 18/09, reclamação real da bancada).
      video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
      audio: false,
    });
  } catch (err) {
    const name = (err as { name?: string }).name;
    onError(
      name === "NotAllowedError"
        ? "Permissão de câmera negada. Libere nas configurações do navegador."
        : name === "NotFoundError"
          ? "Nenhuma câmera encontrada neste aparelho."
          : "Não foi possível abrir a câmera. Verifique se o acesso é por HTTPS."
    );
    return () => {};
  }

  video.srcObject = stream;
  video.setAttribute("playsinline", "true");
  await video.play().catch(() => undefined);

  const detector = await makeDetector();
  const canvas = document.createElement("canvas");
  const context2d = canvas.getContext("2d", { willReadFrequently: true });

  const tick = async () => {
    if (stopped) return;

    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      if (detector) {
        try {
          const found = await detector.detect(video);
          if (found[0]?.rawValue) emit({ value: found[0].rawValue });
        } catch {
          // Falha pontual de decodificação não derruba o laço.
        }
      } else if (context2d) {
        // Reduz pro jsQR também, além da resolução já pedida à câmera — um
        // QR não precisa de muito pixel pra decodificar, e desenhar o quadro
        // JÁ menor no canvas (em vez de cortar depois) é o que realmente
        // baixa o custo por quadro, já que é isso que o jsQR varre.
        const LADO_MAIOR_MAX = 640;
        const escala = Math.min(1, LADO_MAIOR_MAX / Math.max(video.videoWidth, video.videoHeight));
        canvas.width = Math.round(video.videoWidth * escala);
        canvas.height = Math.round(video.videoHeight * escala);
        context2d.drawImage(video, 0, 0, canvas.width, canvas.height);

        const image = context2d.getImageData(0, 0, canvas.width, canvas.height);
        const found = jsQR(image.data, image.width, image.height, {
          inversionAttempts: "dontInvert",
        });

        if (found?.data) emit({ value: found.data });
      }
    }

    frame = requestAnimationFrame(() => void tick());
  };

  frame = requestAnimationFrame(() => void tick());

  return () => {
    stopped = true;
    cancelAnimationFrame(frame);
    stream?.getTracks().forEach((track) => track.stop());
    video.srcObject = null;
  };
}
